import {ArrUtil} from './arr-util'
import {logger} from './logger'
import {TypeUtil} from './type-util'

const maxIterations = 10_000

export class Graph {
  /**
   * Traverses a graph in a depth-first manner
   *
   * @param props.enter Return `false` to stop iterating this node
   */
  static dfs<T>(props: {
    start: T
    adjacent: (node: T) => Iterable<T>
    enter: (node: T) => boolean | undefined | void
    leave?: (node: T) => void
  }): void {
    let i = 0

    const {start, enter, leave, adjacent} = props

    enum CommandType {
      Enter,
      Leave
    }

    interface Command<T> {
      node: T
      type: CommandType
    }

    let stack: Command<T>[] = [{node: start, type: CommandType.Enter}]
    while (stack.length) {
      const command = stack.pop()
      if (!command) {
        continue
      }

      const {node, type} = command
      if (type === CommandType.Enter) {
        const state = enter(node)
        if (state === false) {
          continue
        }

        stack.push({node, type: CommandType.Leave})

        const nodes = Array.from(adjacent(node))
        stack = stack.concat(
          nodes.map(node => ({node, type: CommandType.Enter}))
        )
      } else if (type === CommandType.Leave) {
        leave?.(node)
      }

      // Guard against any cycles or very large hierarchies
      if (i > maxIterations) {
        logger.warn('Graph.dfs, too many iterations, skipping the rest')
        return
      }
      i += 1
    }
  }

  /**
   * Traverses a graph in a breadth-first manner
   *
   * @param props.enter Return `false` to stop iterating this node
   */
  static bfs<T>(props: {
    start: T
    adjacent: (node: T) => Iterable<T>
    enter: (node: T) => boolean | undefined | void
  }): void {
    let i = 0

    const {start, enter, adjacent} = props

    let current: T[] = [start]
    while (current.length) {
      let next: T[] = []
      for (const node of current) {
        const state = enter(node)
        if (state === false) {
          continue
        }

        const nodes = Array.from(adjacent(node))
        next = next.concat(nodes)
      }
      current = next

      // Guard against any cycles or very large hierarchies
      if (i > maxIterations) {
        logger.warn('Graph.bfs, too many iterations, skipping the rest')
        return
      }
      i += 1
    }
  }

  static topologicalSort<T, K extends string | number>(
    xs: Iterable<T>,
    adjacent: (node: T) => Iterable<T>,
    toKey: (node: T) => K
  ): ReadonlyArray<T> {
    const ix = ArrUtil.createIndex(Array.from(xs), toKey)
    const sorted = this.topologicalSortImpl(ix.keys(), key => {
      const x = ix.get(key)
      return x ? Array.from(adjacent(x)).map(toKey) : []
    })
    return sorted.map(key => ix.get(key)).filter(TypeUtil.isTruthy)
  }

  private static topologicalSortImpl<T extends string | number>(
    xs: Iterable<T>,
    adjacent: (node: T) => Iterable<T>
  ): ReadonlyArray<T> {
    const result: T[] = []

    const notMarked = new Set(xs)
    const observed = new Set<T>()

    while (notMarked.size) {
      const [start] = notMarked
      Graph.dfs({
        start,
        enter: x => {
          if (!notMarked.has(x) || observed.has(x)) {
            return false
          }
          observed.add(x)
          return true
        },
        leave: x => {
          notMarked.delete(x)
          result.push(x)
        },
        adjacent
      })
    }

    return result.reverse()
  }
}
